import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { checkBusinessRegistration, saveOnboardingStep, completeOnboardingWithData } from '../store/slices/authSlice';
import { toast } from 'react-toastify';
import './BusinessSetup.css';

const BusinessSetup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, businessRegistered, loading } = useSelector(state => state.auth);

  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [setupError, setSetupError] = useState(null);
  const [completedSteps, setCompletedSteps] = useState(new Set()); // Track completed steps
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    businessDescription: user?.businessDescription || '',
    timezone: user?.timezone || 'UTC',
    businessHours: user?.businessHours || {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: false, start: '09:00', end: '17:00' },
      sunday: { enabled: false, start: '09:00', end: '17:00' }
    },
    businessWebsite: user?.businessWebsite || '',
    businessAddress: user?.businessAddress || '',
    autoReplyMessage: user?.autoReplyMessage || ''
  });
  
  const steps = [
    { number: 1, title: 'Introduction', icon: '👋' },
    { number: 2, title: 'Business Info', icon: '🏢' },
    { number: 3, title: 'Business Hours', icon: '🕐' },
    { number: 4, title: 'Finish', icon: '✅' }
  ];

  // Check if business is already registered
  useEffect(() => {
    const checkStatus = async () => {
      try {
        await dispatch(checkBusinessRegistration()).unwrap();
      } catch (err) {
        console.error('Failed to check business registration:', err);
      }
    };

    checkStatus();
  }, [dispatch]);

  // Redirect if business is already registered
  useEffect(() => {
    if (businessRegistered && !loading) {
      navigate('/dashboard', { replace: true });
    }
  }, [businessRegistered, loading, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBusinessHoursChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: {
          ...prev.businessHours[day],
          [field]: value
        }
      }
    }));
  };

  const validateStep2 = () => {
    const newErrors = {};

    // Business description validation
    if (!formData.businessDescription.trim()) {
      newErrors.businessDescription = 'Business description is required';
    } else if (formData.businessDescription.trim().length < 10) {
      newErrors.businessDescription = 'Business description must be at least 10 characters';
    } else if (formData.businessDescription.length > 1024) {
      newErrors.businessDescription = 'Business description cannot exceed 1024 characters';
    }

    // Business address validation (optional but if provided, must be valid)
    if (formData.businessAddress.trim() && formData.businessAddress.trim().length < 5) {
      newErrors.businessAddress = 'Address must be at least 5 characters if provided';
    }

    // Website validation (optional but if provided, must be valid URL)
    if (formData.businessWebsite.trim()) {
      const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
      if (!urlPattern.test(formData.businessWebsite)) {
        newErrors.businessWebsite = 'Please enter a valid website URL (e.g., https://www.example.com)';
      }
    }

    // Timezone validation
    if (!formData.timezone) {
      newErrors.timezone = 'Please select a timezone';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    const hours = formData.businessHours;
    let hasEnabledDay = false;

    // Check if at least one day is enabled
    for (const day in hours) {
      if (hours[day].enabled) {
        hasEnabledDay = true;
        // Validate time format
        if (!hours[day].start || !hours[day].end) {
          newErrors[`${day}_time`] = 'Please set both start and end times';
        } else if (hours[day].start >= hours[day].end) {
          newErrors[`${day}_time`] = 'End time must be after start time';
        }
      }
    }

    if (!hasEnabledDay) {
      newErrors.businessHours = 'Please enable at least one business day';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div className="business-setup-container">
      <div className="business-setup-card">
        {/* Step Indicator */}
        <div className="step-indicator">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className={`step-item${completedSteps.has(step.number) ? ' completed' : ''}${currentStep === step.number ? ' current' : ''}`}>
                <div className="step-circle">
                  {completedSteps.has(step.number) ? '✓' : step.number}
                </div>
                <div className="step-label">
                  <div className="step-number-label">Step {step.number}</div>
                  <div className="step-title">{step.title}</div>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className={`step-connector${completedSteps.has(step.number) ? ' completed' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Introduction */}
        {currentStep === 1 && (
          <div className="setup-step">
            <div className="setup-icon">👋</div>
            <h1>Welcome to Hawktrion!</h1>
            <p className="subtitle">Set up your WhatsApp Business Account in 4 simple steps</p>
            
            <div className="feature-grid">
              <div className="feature-card">
                <div className="feature-icon-wrap">📱</div>
                <div>
                  <strong>Message Customers</strong>
                  <p>Send WhatsApp messages directly to your customers</p>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon-wrap">📝</div>
                <div>
                  <strong>Message Templates</strong>
                  <p>Create approved templates for outbound messaging</p>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon-wrap">📊</div>
                <div>
                  <strong>Analytics</strong>
                  <p>Track delivery, opens, and campaign performance</p>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon-wrap">🎯</div>
                <div>
                  <strong>Campaigns</strong>
                  <p>Run targeted promotions at scale</p>
                </div>
              </div>
            </div>

            {setupError && (
              <div className="setup-error">
                <p>{setupError}</p>
              </div>
            )}

            <div className="step-actions">
              <button
                className="setup-button primary"
                onClick={() => {
                  setCompletedSteps(prev => new Set([...prev, 1]));
                  setCurrentStep(2);
                }}
              >
                Get Started →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Business Information */}
        {currentStep === 2 && (
          <div className="setup-step">
            <div className="setup-icon">🏢</div>
            <h1>Business Information</h1>
            <p className="subtitle">Tell us about your business</p>
            
            {setupError && (
              <div className="error-banner">
                <span className="error-icon">⚠️</span>
                <span>{setupError}</span>
              </div>
            )}
            
            <div className="setup-form">
              <div className="form-group">
                <label htmlFor="businessDescription">
                  Business Description <span className="required">*</span>
                </label>
                <textarea
                  id="businessDescription"
                  value={formData.businessDescription}
                  onChange={(e) => handleInputChange('businessDescription', e.target.value)}
                  placeholder="Describe what your business does (e.g., We offer premium coffee and pastries)"
                  rows="3"
                  maxLength="1024"
                  className={errors.businessDescription ? 'error' : ''}
                />
                <small className="char-count">
                  {formData.businessDescription.length}/1024 characters
                </small>
                {errors.businessDescription && (
                  <span className="error-message">{errors.businessDescription}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="businessAddress">Business Address <span className="optional">(Optional)</span></label>
                <input
                  id="businessAddress"
                  type="text"
                  value={formData.businessAddress}
                  onChange={(e) => handleInputChange('businessAddress', e.target.value)}
                  placeholder="123 Main Street, City, State, ZIP"
                  maxLength="256"
                  className={errors.businessAddress ? 'error' : ''}
                />
                {errors.businessAddress && (
                  <span className="error-message">{errors.businessAddress}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="businessWebsite">Website <span className="optional">(Optional)</span></label>
                <input
                  id="businessWebsite"
                  type="url"
                  value={formData.businessWebsite}
                  onChange={(e) => handleInputChange('businessWebsite', e.target.value)}
                  placeholder="https://www.yourwebsite.com"
                  maxLength="256"
                  className={errors.businessWebsite ? 'error' : ''}
                />
                {errors.businessWebsite && (
                  <span className="error-message">{errors.businessWebsite}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="timezone">
                  Timezone <span className="required">*</span>
                </label>
                <select
                  id="timezone"
                  value={formData.timezone}
                  onChange={(e) => handleInputChange('timezone', e.target.value)}
                  className={errors.timezone ? 'error' : ''}
                >
                  <option value="UTC">UTC − Coordinated Universal Time (UTC+0)</option>
                  <optgroup label="Americas">
                    <option value="HST">HST − Hawaii Standard Time (UTC−10)</option>
                    <option value="AKST">AKST − Alaska Standard Time (UTC−9)</option>
                    <option value="PST">PST − Pacific Standard Time (UTC−8)</option>
                    <option value="MST">MST − Mountain Standard Time (UTC−7)</option>
                    <option value="CST">CST − Central Standard Time (UTC−6)</option>
                    <option value="EST">EST − Eastern Standard Time (UTC−5)</option>
                    <option value="AST">AST − Atlantic Standard Time (UTC−4)</option>
                    <option value="BRT">BRT − Brasilia Time (UTC−3)</option>
                    <option value="ART">ART − Argentina Time (UTC−3)</option>
                  </optgroup>
                  <optgroup label="Europe / Africa">
                    <option value="GMT">GMT − Greenwich Mean Time (UTC+0)</option>
                    <option value="CET">CET − Central European Time (UTC+1)</option>
                    <option value="EET">EET − Eastern European Time (UTC+2)</option>
                    <option value="MSK">MSK − Moscow Standard Time (UTC+3)</option>
                    <option value="WAT">WAT − West Africa Time (UTC+1)</option>
                    <option value="CAT">CAT − Central Africa Time (UTC+2)</option>
                    <option value="EAT">EAT − East Africa Time (UTC+3)</option>
                    <option value="SAST">SAST − South Africa Standard Time (UTC+2)</option>
                  </optgroup>
                  <optgroup label="Asia">
                    <option value="GST">GST − Gulf Standard Time (UTC+4)</option>
                    <option value="PKT">PKT − Pakistan Standard Time (UTC+5)</option>
                    <option value="IST">IST − India Standard Time (UTC+5:30)</option>
                    <option value="BST">BST − Bangladesh Standard Time (UTC+6)</option>
                    <option value="ICT">ICT − Indochina Time (UTC+7)</option>
                    <option value="WIB">WIB − Western Indonesia Time (UTC+7)</option>
                    <option value="CST_CN">CST − China Standard Time (UTC+8)</option>
                    <option value="SGT">SGT − Singapore Time (UTC+8)</option>
                    <option value="HKT">HKT − Hong Kong Time (UTC+8)</option>
                    <option value="JST">JST − Japan Standard Time (UTC+9)</option>
                    <option value="KST">KST − Korea Standard Time (UTC+9)</option>
                  </optgroup>
                  <optgroup label="Oceania">
                    <option value="AWST">AWST − Australian Western Standard Time (UTC+8)</option>
                    <option value="AEST">AEST − Australian Eastern Standard Time (UTC+10)</option>
                    <option value="NZST">NZST − New Zealand Standard Time (UTC+12)</option>
                  </optgroup>
                </select>
                {errors.timezone && (
                  <span className="error-message">{errors.timezone}</span>
                )}
              </div>
            </div>

            <div className="step-actions">
              <button 
                className="setup-button secondary" 
                onClick={() => {
                  setCurrentStep(1);
                  setErrors({});
                  setSetupError(null);
                }}
                disabled={isProcessing}
              >
                ← Back
              </button>
              <button 
                className="setup-button primary" 
                onClick={async () => {
                  if (!validateStep2()) {
                    toast.error('⚠️ Please fix the errors below');
                    return;
                  }
                  
                  try {
                    setIsProcessing(true);
                    setSetupError(null);
                    
                    await dispatch(saveOnboardingStep({ 
                      step: 2, 
                      data: {
                        businessDescription: formData.businessDescription,
                        businessAddress: formData.businessAddress,
                        businessWebsite: formData.businessWebsite,
                        timezone: formData.timezone
                      }
                    })).unwrap();
                    
                    setCompletedSteps(prev => new Set([...prev, 2]));
                    setCurrentStep(3);
                    setErrors({});
                  } catch (err) {
                    const errorMsg = err?.message || 'Failed to save business information';
                    setSetupError(errorMsg);
                    toast.error(`❌ ${errorMsg}`);
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                disabled={isProcessing || !formData.businessDescription.trim()}
              >
                {isProcessing ? 'Saving...' : 'Next →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Business Hours */}
        {currentStep === 3 && (
          <div className="setup-step">
            <div className="setup-icon">🕐</div>
            <h1>Business Hours</h1>
            <p className="subtitle">Set your operating hours and away message</p>
            
            {setupError && (
              <div className="error-banner">
                <span className="error-icon">⚠️</span>
                <span>{setupError}</span>
              </div>
            )}
            
            <div className="business-hours-form">
              <div className="hours-header">
                <p className="hours-info">Enable the days your business is open and set your operating hours</p>
                {errors.businessHours && (
                  <span className="error-message">{errors.businessHours}</span>
                )}
              </div>
              
              <div className="hours-quick-actions">
                <button
                  type="button"
                  className="quick-action-btn"
                  onClick={() => {
                    ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].forEach(day => {
                      handleBusinessHoursChange(day, 'enabled', true);
                      handleBusinessHoursChange(day, 'start', '09:00');
                      handleBusinessHoursChange(day, 'end', '17:00');
                    });
                    ['saturday', 'sunday'].forEach(day => handleBusinessHoursChange(day, 'enabled', false));
                  }}
                  disabled={isProcessing}
                >
                  Mon–Fri 9–5
                </button>
                <button
                  type="button"
                  className="quick-action-btn"
                  onClick={() => {
                    Object.keys(formData.businessHours).forEach(day => handleBusinessHoursChange(day, 'enabled', true));
                  }}
                  disabled={isProcessing}
                >
                  Every day
                </button>
                <button
                  type="button"
                  className="quick-action-btn"
                  onClick={() => {
                    Object.keys(formData.businessHours).forEach(day => handleBusinessHoursChange(day, 'enabled', false));
                  }}
                  disabled={isProcessing}
                >
                  Clear all
                </button>
              </div>
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                <div key={day} className="hours-item">
                  <div className="day-label">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.businessHours[day].enabled}
                        onChange={(e) => handleBusinessHoursChange(day, 'enabled', e.target.checked)}
                        disabled={isProcessing}
                      />
                      <span className="day-name">
                        {day.charAt(0).toUpperCase() + day.slice(1)}
                      </span>
                    </label>
                  </div>
                  {formData.businessHours[day].enabled && (
                    <div className="hours-inputs-wrapper">
                      <div className="hours-inputs">
                        <input
                          type="time"
                          value={formData.businessHours[day].start}
                          onChange={(e) => handleBusinessHoursChange(day, 'start', e.target.value)}
                          disabled={isProcessing}
                        />
                        <span className="to-text">to</span>
                        <input
                          type="time"
                          value={formData.businessHours[day].end}
                          onChange={(e) => handleBusinessHoursChange(day, 'end', e.target.value)}
                          disabled={isProcessing}
                        />
                      </div>
                      {errors[`${day}_time`] && (
                        <span className="error-message">{errors[`${day}_time`]}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="setup-form">
              <div className="form-group">
                <label htmlFor="autoReplyMessage">
                  Auto Reply Message <span className="optional">(Optional)</span>
                </label>
                <textarea
                  id="autoReplyMessage"
                  value={formData.autoReplyMessage}
                  onChange={(e) => handleInputChange('autoReplyMessage', e.target.value)}
                  placeholder="E.g., Thank you for your message! We'll respond during business hours."
                  rows="3"
                  maxLength="1024"
                  disabled={isProcessing}
                />
                <small className="char-count">
                  {formData.autoReplyMessage.length}/1024 characters
                </small>
              </div>
            </div>

            <div className="step-actions">
              <button 
                className="setup-button secondary" 
                onClick={() => {
                  setCurrentStep(2);
                  setErrors({});
                  setSetupError(null);
                }}
                disabled={isProcessing}
              >
                ← Back
              </button>
              <button 
                className="setup-button primary" 
                onClick={async () => {
                  if (!validateStep3()) {
                    toast.error('⚠️ Please fix the errors below');
                    return;
                  }
                  
                  try {
                    setIsProcessing(true);
                    setSetupError(null);
                    
                    await dispatch(saveOnboardingStep({ 
                      step: 3, 
                      data: {
                        businessHours: formData.businessHours,
                        autoReplyMessage: formData.autoReplyMessage
                      }
                    })).unwrap();
                    
                    setCompletedSteps(prev => new Set([...prev, 3]));
                    setCurrentStep(4);
                    setErrors({});
                  } catch (err) {
                    const errorMsg = err?.message || 'Failed to save business hours';
                    setSetupError(errorMsg);
                    toast.error(`❌ ${errorMsg}`);
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                disabled={isProcessing}
              >
                {isProcessing ? 'Saving...' : 'Next →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Verification */}
        {currentStep === 4 && (
          <div className="setup-step">
            <div className="success-hero">
              <div className="success-check-wrap">
                <svg className="success-svg" viewBox="0 0 52 52">
                  <circle className="success-circle" cx="26" cy="26" r="25" fill="none" />
                  <path className="success-checkmark" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                </svg>
              </div>
              <h1>You're all set!</h1>
              <p className="success-sub">Your WhatsApp Business Account is configured and ready</p>

              <div className="summary-cards">
                <div className="summary-card-item">
                  <span className="sc-icon">🏢</span>
                  <div className="sc-content">
                    <div className="sc-label">Description</div>
                    <div className="sc-value">{formData.businessDescription || '—'}</div>
                  </div>
                </div>
                <div className="summary-card-item">
                  <span className="sc-icon">🌍</span>
                  <div className="sc-content">
                    <div className="sc-label">Timezone</div>
                    <div className="sc-value">{formData.timezone}</div>
                  </div>
                </div>
                {formData.businessAddress && (
                  <div className="summary-card-item">
                    <span className="sc-icon">📍</span>
                    <div className="sc-content">
                      <div className="sc-label">Address</div>
                      <div className="sc-value">{formData.businessAddress}</div>
                    </div>
                  </div>
                )}
                {formData.businessWebsite && (
                  <div className="summary-card-item">
                    <span className="sc-icon">🌐</span>
                    <div className="sc-content">
                      <div className="sc-label">Website</div>
                      <div className="sc-value">{formData.businessWebsite}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="unlock-section">
                <p className="unlock-title">You now have access to:</p>
                <div className="unlock-tags">
                  <span className="unlock-tag">📨 Templates</span>
                  <span className="unlock-tag">💬 Messaging</span>
                  <span className="unlock-tag">📊 Analytics</span>
                  <span className="unlock-tag">🎯 Campaigns</span>
                  <span className="unlock-tag">⏰ Business Hours</span>
                  <span className="unlock-tag">🤖 Auto-Replies</span>
                </div>
              </div>
            </div>

            {setupError && (
              <div className="setup-error">
                <p>{setupError}</p>
              </div>
            )}

            <div className="step-actions">
              <button 
                className="setup-button primary" 
                onClick={async () => {
                  setIsProcessing(true);
                  try {
                    await dispatch(completeOnboardingWithData({
                      businessDescription: formData.businessDescription,
                      businessAddress: formData.businessAddress,
                      businessWebsite: formData.businessWebsite,
                      timezone: formData.timezone,
                      businessHours: formData.businessHours,
                      autoReplyMessage: formData.autoReplyMessage
                    })).unwrap();
                    
                    setCompletedSteps(prev => new Set([...prev, 4]));
                    toast.success('Onboarding completed successfully!');
                    
                    // Add a small delay for better UX
                    setTimeout(() => {
                      navigate('/dashboard', { replace: true });
                    }, 500);
                  } catch (err) {
                    toast.error('Failed to complete onboarding');
                    setSetupError(err?.message || 'Error completing setup. Please try again.');
                    setIsProcessing(false);
                  }
                }}
                disabled={isProcessing}
              >
                {isProcessing ? 'Completing Setup...' : 'Go to Dashboard →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessSetup;
